import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, X, Maximize2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal, RevealGroup, RevealItem } from "@/components/motion/Reveal";
import { CountUp } from "@/components/motion/CountUp";
import { SpotlightCard } from "@/components/motion/SpotlightCard";
import { projects } from "@/data/content";

const ease = [0.16, 1, 0.3, 1];

const Shot = ({ src, label, onOpen, testid }) => (
  <button data-testid={testid} onClick={() => onOpen(src, label)} className="group/shot block w-full text-left">
    <div className="relative rounded-lg border border-[#1e1e1e] bg-[#141414] overflow-hidden">
      <div className="overflow-hidden">
        <img src={src} alt={label} loading="lazy" className="w-full object-cover object-top transition-transform duration-[1.2s] group-hover/shot:scale-105" />
      </div>
      <span className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-md bg-[#0a0a0a]/80 text-white opacity-0 backdrop-blur transition-opacity group-hover/shot:opacity-100">
        <Maximize2 className="h-4 w-4" />
      </span>
      {label && (
        <div className="flex items-center justify-between border-t border-[#1e1e1e] px-4 py-2.5">
          <span className="text-sm font-medium text-white">{label}</span>
        </div>
      )}
    </div>
  </button>
);

const CaseStudy = ({ cs, onOpen }) => (
  <>
    {/* Overview + stack */}
    <section data-testid="case-overview" className="bg-[#0a0a0a] border-t border-[#1e1e1e]">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <p className="eyebrow">Overview</p>
            <h2 className="mt-5 text-3xl lg:text-4xl font-semibold tracking-[-0.03em] leading-[1.1] text-white">{cs.headline}</h2>
            <p className="mt-6 text-base text-[#a7a7a7] leading-relaxed">{cs.overview}</p>
            <div className="mt-8">
              <p className="font-mono text-[11px] uppercase tracking-wider text-[#7c7c7c]">Roles served</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {cs.roles.map((r) => (
                  <span key={r} className="rounded-md text-sm text-[#a7a7a7] bg-[#141414] border border-[#1e1e1e] px-3 py-1.5">{r}</span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="rounded-lg border border-[#1e1e1e] bg-[#141414] p-6">
              <p className="font-mono text-[11px] uppercase tracking-wider text-[#15b351]">Stack at a glance</p>
              <dl className="mt-4 divide-y divide-[#1e1e1e]">
                {Object.entries(cs.stack).map(([k, v]) => (
                  <div key={k} className="py-3">
                    <dt className="text-sm font-semibold text-white">{k}</dt>
                    <dd className="mt-1 text-sm text-[#7c7c7c] leading-relaxed">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* Feature index */}
    <section data-testid="case-index" className="bg-[#0a0a0a] border-t border-[#1e1e1e]">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-16">
        <p className="eyebrow">16 Features · Full Case Study</p>
        <RevealGroup stagger={0.03} className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1e1e1e] rounded-lg overflow-hidden border border-[#1e1e1e]">
          {cs.features.map((f) => (
            <RevealItem key={f.id}>
              <a href={`#feat-${f.id}`} data-testid={`case-index-${f.id}`} className="flex h-full items-start gap-3 bg-[#0a0a0a] p-4 hover:bg-[#141414] transition-colors group">
                <span className="font-mono text-xs text-[#15b351]">{f.n}</span>
                <span className="text-sm font-medium text-[#a7a7a7] group-hover:text-white transition-colors">{f.title}</span>
              </a>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </section>

    {/* Feature deep-dives */}
    {cs.features.map((f, idx) => (
      <section
        key={f.id}
        id={`feat-${f.id}`}
        data-testid={`case-feature-${f.id}`}
        className={`scroll-mt-20 border-t border-[#1e1e1e] ${idx % 2 === 1 ? "bg-[#0c0c0c]" : "bg-[#0a0a0a]"}`}
      >
        <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-20 lg:py-24">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5 lg:sticky lg:top-24 lg:self-start">
                {f.category && <p className="eyebrow mb-3">{f.category}</p>}
                <span className="font-mono text-sm text-[#454545]">{f.n}</span>
                <h3 className="mt-3 text-3xl lg:text-4xl font-semibold tracking-[-0.03em] leading-[1.08] text-white">{f.title}</h3>
                <p className="mt-5 text-base text-[#a7a7a7] leading-relaxed">{f.purpose}</p>

                <div className="mt-7 space-y-4">
                  <div className="rounded-lg border border-[#1e1e1e] bg-[#141414] p-4">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-[#7c7c7c]">Problem solved</p>
                    <p className="mt-1.5 text-sm text-[#a7a7a7] leading-relaxed">{f.problem}</p>
                  </div>
                  <div className="rounded-lg border border-[#15b351]/20 bg-[#15b351]/[0.04] p-4">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-[#15b351]">Business value</p>
                    <p className="mt-1.5 text-sm text-[#a7a7a7] leading-relaxed">{f.value}</p>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  {f.tech.map((t) => (
                    <span key={t} className="rounded-md font-mono text-[10px] uppercase tracking-wide text-[#a7a7a7] bg-[#0a0a0a] border border-[#1e1e1e] px-2 py-1">{t}</span>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-7 space-y-5">
                {f.shots.filter((s) => s.label !== "Mobile").map((s, i) => (
                  <Shot key={s.src} src={s.src} label={s.label} onOpen={onOpen} testid={`case-shot-${f.id}-${i}`} />
                ))}
                {f.shots.some((s) => s.label === "Mobile") && (
                  <div className="flex flex-wrap items-start gap-3 pt-1">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-[#7c7c7c] w-full">Mobile view</span>
                    {f.shots.filter((s) => s.label === "Mobile").map((s, i) => (
                      <button
                        key={s.src}
                        data-testid={`case-mobile-${f.id}-${i}`}
                        onClick={() => onOpen(s.src, `${f.title} — Mobile`)}
                        className="group/mob relative w-[92px] shrink-0 overflow-hidden rounded-lg border border-[#1e1e1e] bg-[#141414] transition-colors hover:border-[#15b351]/50"
                      >
                        <div className="h-[150px] overflow-hidden">
                          <img src={s.src} alt={`${f.title} mobile`} loading="lazy" className="w-full object-cover object-top" />
                        </div>
                        <span className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover/mob:opacity-100">
                          <Maximize2 className="h-4 w-4 text-white" />
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    ))}
  </>
);

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = projects.find((p) => p.slug === slug);
  const [lightbox, setLightbox] = useState(null);
  if (!project) return <Navigate to="/" replace />;

  const openLightbox = (src, label) => setLightbox({ src, label });

  return (
    <div className="bg-[#0a0a0a]">
      <Navbar />

      <section className="relative overflow-hidden bg-[#0a0a0a] pt-16">
        <div className="absolute inset-0 gp-blueprint gp-radial-fade" />
        <div className="gp-aurora-a absolute top-0 right-0 h-[420px] w-[600px] rounded-full bg-[#15b351]/10 blur-[130px]" />
        <div className="relative mx-auto max-w-[1200px] px-6 lg:px-8 pt-20 pb-16 lg:pt-24 lg:pb-20">
          <Link data-testid="project-back-btn" to="/#projects" className="inline-flex items-center gap-2 text-sm text-[#7c7c7c] hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" /> All projects
          </Link>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }} className="lg:col-span-6">
              <div className="flex items-center gap-3">
                <p className="eyebrow">{project.category}</p>
                <span className="flex items-center gap-1.5 rounded-full border border-[#15b351]/30 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[#15b351]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#15b351] gp-blink" /> {project.status}
                </span>
              </div>
              <h1 className="mt-5 text-5xl lg:text-[64px] font-semibold tracking-[-0.035em] leading-none text-white">{project.name}</h1>
              <p className="mt-6 text-lg text-[#a7a7a7] leading-relaxed max-w-xl">{project.summary}</p>
              <div className="mt-7 flex flex-wrap gap-2">
                {project.tags.map((t) => (
                  <span key={t} className="rounded-md font-mono text-[11px] uppercase tracking-wide text-[#a7a7a7] border border-[#313131] px-2.5 py-1.5">{t}</span>
                ))}
              </div>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link data-testid="project-contact-btn" to="/#contact" className="gp-shine inline-flex items-center justify-center gap-2 rounded-lg bg-white px-6 py-3.5 text-sm font-medium text-[#0a0a0a] hover:bg-[#15b351] hover:text-white transition-colors">
                  Build something like this <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.8, delay: 0.12, ease }} className="lg:col-span-6">
              <button onClick={() => openLightbox(project.cover, project.name)} className="block w-full text-left">
                <div className="gp-float rounded-lg border border-[#1e1e1e] overflow-hidden bg-[#141414] shadow-[0_30px_90px_-40px_rgba(21,179,81,0.5)]">
                  <img src={project.cover} alt={project.name} className="w-full object-cover object-top max-h-[460px]" />
                </div>
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {project.metrics?.length > 0 && (
        <section className="bg-[#0a0a0a] border-t border-[#1e1e1e]">
          <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#1e1e1e] border-x border-[#1e1e1e]">
              {project.metrics.map((m, i) => (
                <div key={m.label} data-testid={`project-metric-${i}`} className="bg-[#0a0a0a] py-10 px-6 transition-colors hover:bg-[#141414]">
                  <CountUp to={m.to} prefix={m.prefix || ""} suffix={m.suffix || ""} className="text-4xl lg:text-5xl font-semibold tracking-[-0.03em] text-white" />
                  <p className="mt-2 font-mono text-[11px] uppercase tracking-wider text-[#7c7c7c]">{m.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {project.caseStudy ? (
        <CaseStudy cs={project.caseStudy} onOpen={openLightbox} />
      ) : (
        <>
          <section data-testid="project-features" className="bg-[#0a0a0a] border-t border-[#1e1e1e]">
            <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-20 lg:py-28">
              <Reveal className="max-w-2xl">
                <p className="eyebrow">Highlights</p>
                <h2 className="mt-5 text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05] text-white">What we built</h2>
              </Reveal>
              <RevealGroup className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1e1e1e] rounded-lg overflow-hidden border border-[#1e1e1e]">
                {project.features.map((f, i) => (
                  <RevealItem key={f.title}>
                    <SpotlightCard testid={`project-feature-${i}`} className="h-full bg-[#0a0a0a] p-7 hover:bg-[#141414] transition-colors">
                      <span className="font-mono text-xs text-[#15b351]">{String(i + 1).padStart(2, "0")}</span>
                      <h3 className="mt-4 text-xl font-semibold tracking-tight text-white">{f.title}</h3>
                      <p className="mt-2.5 text-sm text-[#a7a7a7] leading-relaxed">{f.text}</p>
                    </SpotlightCard>
                  </RevealItem>
                ))}
              </RevealGroup>
            </div>
          </section>
          {project.gallery?.length > 0 && (
            <section data-testid="project-gallery" className="bg-[#0a0a0a] border-t border-[#1e1e1e]">
              <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-20 lg:py-28">
                <Reveal className="max-w-2xl">
                  <p className="eyebrow">Gallery</p>
                  <h2 className="mt-5 text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05] text-white">Inside the product</h2>
                </Reveal>
                <RevealGroup className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {project.gallery.map((g, i) => (
                    <RevealItem key={g.src}>
                      <Shot src={g.src} label={g.label} onOpen={openLightbox} testid={`project-shot-${i}`} />
                    </RevealItem>
                  ))}
                </RevealGroup>
              </div>
            </section>
          )}
        </>
      )}

      <section className="bg-[#0a0a0a] border-t border-[#1e1e1e]">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-20 lg:py-24">
          <div className="rounded-2xl border border-[#1e1e1e] bg-[#141414] p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05] text-white">Have a system like this in mind?</h2>
              <p className="mt-6 text-base text-[#a7a7a7] max-w-md">We design and build operational software end to end — from discovery to deployment and optimization.</p>
            </div>
            <div className="lg:justify-self-end">
              <div className="space-y-3">
                {["Full-stack build (React · FastAPI · MongoDB)", "Audit-logged, production-grade systems", "End-to-end delivery & long-term partnership"].map((p) => (
                  <div key={p} className="flex items-center gap-3 text-base text-white">
                    <Check className="h-5 w-5 text-[#15b351]" strokeWidth={3} /> {p}
                  </div>
                ))}
              </div>
              <Link data-testid="project-cta-btn" to="/#contact" className="gp-shine mt-8 inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3.5 text-sm font-medium text-[#0a0a0a] hover:bg-[#15b351] hover:text-white transition-colors">
                Book a Free Consultation <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 lg:p-10 cursor-zoom-out"
          >
            <button data-testid="lightbox-close" onClick={() => setLightbox(null)} className="absolute top-5 right-5 flex h-10 w-10 items-center justify-center rounded-lg border border-[#313131] text-white hover:bg-white/10 transition-colors">
              <X className="h-5 w-5" />
            </button>
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.25, ease }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-full w-full max-w-6xl overflow-auto rounded-lg border border-[#1e1e1e] bg-[#141414] cursor-default"
            >
              {lightbox.label && (
                <div className="sticky top-0 flex items-center justify-between border-b border-[#1e1e1e] bg-[#141414] px-5 py-3">
                  <span className="text-sm font-medium text-white">{lightbox.label}</span>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-[#454545]">Click outside to close</span>
                </div>
              )}
              <img src={lightbox.src} alt={lightbox.label} className="w-full" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
